import { XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { useViewHealthMarker } from '@/lib/view-health';

import { ConnectionStatusPanel } from './components/ConnectionStatusPanel';
import { MailchimpSetupWizard } from './components/MailchimpSetupWizard';
import { useMailchimpStatus } from './hooks/useMailchimpStatus';
import { MailchimpIcon } from './MailchimpIcon';

const MAILCHIMP_ERROR_MESSAGES: Record<string, string> = {
  token_exchange_failed:
    "We couldn't complete Mailchimp authentication. Try again.",
  metadata_failed:
    "We couldn't retrieve Mailchimp account information. Try again.",
  connect_race:
    'Another connection attempt is already in progress. Please wait and try again.',
  user_mismatch:
    'The Mailchimp account is associated with a different user. Please try again.',
};

const DEFAULT_MAILCHIMP_ERROR_MESSAGE =
  'An unexpected error occurred. Please try connecting again.';

const LAUNCHER_TILE_CLASS =
  'flex h-12 w-12 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

export function MailchimpSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  const [searchParams, setSearchParams] = useSearchParams();
  useViewHealthMarker('settings-section-ready', { ready: true });

  const status = useMailchimpStatus(orgId);

  const [oauthError, setOauthError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [setupDone, setSetupDone] = useState(false);

  // Resume the setup wizard after the OAuth redirect lands back here.
  useEffect(() => {
    const oauth = searchParams.get('mailchimp_oauth');
    const error = searchParams.get('mailchimp_error');
    setOauthError(error);
    if (oauth === 'success') {
      setWizardOpen(true);
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('mailchimp_oauth');
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const dismissError = useCallback(() => {
    setOauthError(null);
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('mailchimp_error');
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const openWizard = useCallback(() => {
    setWizardOpen(true);
  }, []);

  const handleWizardOpenChange = useCallback((open: boolean) => {
    setWizardOpen(open);
  }, []);

  const handleFinished = useCallback(() => {
    setSetupDone(true);
  }, []);

  const connected = status.data?.connected ?? false;
  const hasAudience = !!status.data?.audience_id;
  const bulkDone = !!status.data?.bulk_completed_at;
  const isComplete = connected && hasAudience && (bulkDone || setupDone);

  // Where the wizard resumes from when (re)opened.
  const defaultStepId = !connected || !hasAudience ? 'connect' : 'fields';

  return (
    <div className='space-y-8'>
      <header>
        <h2 className='text-lg font-semibold text-fg-primary'>Mailchimp</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Sync your audience with Ahoy contacts.
        </p>
      </header>

      <div className='space-y-6'>
        {oauthError && (
          <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
            <div className='flex items-start gap-3'>
              <XCircle className='mt-0.5 h-5 w-5 shrink-0 text-utility-error-600' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-error-900'>
                  We couldn't connect Mailchimp
                </p>
                <p className='mt-1 text-sm text-utility-error-700'>
                  {MAILCHIMP_ERROR_MESSAGES[oauthError] ??
                    DEFAULT_MAILCHIMP_ERROR_MESSAGE}
                </p>
              </div>
              <CloseButton
                onPress={dismissError}
                label='Dismiss'
                size='sm'
                className='-my-1.5 -mr-1.5 shrink-0'
              />
            </div>
          </div>
        )}

        {!isAdmin && !isComplete && (
          <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
            <p className='text-sm text-utility-warning-700'>
              Mailchimp can only be connected by an account admin.
            </p>
          </div>
        )}

        {isComplete && orgId !== undefined && (
          <ConnectionStatusPanel orgId={orgId} />
        )}

        {!isComplete && isAdmin && orgId !== undefined && (
          <div className='max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6'>
            <div className='flex items-start gap-4'>
              <span className={LAUNCHER_TILE_CLASS}>
                <MailchimpIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
              </span>
              <div className='flex-1'>
                <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
                  {connected
                    ? 'Finish setting up Mailchimp'
                    : 'Connect your Mailchimp audience'}
                </h3>
                <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
                  {connected
                    ? 'Your account is connected – pick an audience and field mappings to start syncing.'
                    : 'Keep a Mailchimp audience in two-way sync with your Ahoy contacts.'}
                </p>
                <div className='mt-4'>
                  <Button color='primary' size='md' onClick={openWizard}>
                    {connected ? 'Continue setup' : 'Set up Mailchimp'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Always mounted for admins so reconnecting an already-set-up audience
            still runs the full wizard (field mappings + backfill choice) instead
            of jumping straight to the status panel. */}
        {isAdmin && orgId !== undefined && (
          <MailchimpSetupWizard
            orgId={orgId}
            isOpen={wizardOpen}
            onOpenChange={handleWizardOpenChange}
            defaultStepId={defaultStepId}
            onFinished={handleFinished}
          />
        )}
      </div>
    </div>
  );
}
